import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useAuth } from "@/hooks/useAuth"
import { useToast } from "@/hooks/use-toast"
import { getApiErrorMessage } from "@/lib/api"
import {
  applyActionLocally,
  BACKLOG_PAGE,
  canUseOutreach,
  localDay,
  regroupToday,
  tabOf,
  type OutreachAction,
  windowThresholds,
  type OutreachProspect,
  type OutreachSettings,
  type InboxView,
  type StageTab,
  type TodayGroups,
} from "@/lib/outreach"
import {
  outreachApi,
  type OutreachActionBody,
  type OutreachFilters,
  type OutreachListParams,
  type OutreachPage,
  type OutreachRunFilters,
  type OutreachSettingsBody,
} from "@/lib/outreach-api"

/**
 * Outreach data: the header numbers, today's queue, one stage's list and one
 * prospect. Actions move the row at once (today's groups, the stage lists and
 * the open panel) and refetch everything outreach afterwards.
 */

export function useOutreachEnabled() {
  const { user } = useAuth()
  return canUseOutreach(user)
}

export function useOutreachStats(filters: OutreachFilters) {
  return useQuery({
    queryKey: ["outreach", "stats", filters],
    queryFn: () => outreachApi.stats(filters),
    enabled: useOutreachEnabled(),
    staleTime: 30 * 1000,
  })
}

export function useOutreachToday(filters: OutreachFilters, enabled = true) {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "today", filters],
    queryFn: () => outreachApi.today(filters),
    enabled: on && enabled,
    staleTime: 30 * 1000,
    // Researching rows turn into drafts on their own (the research agent runs every 30 min).
    refetchInterval: 2 * 60 * 1000,
  })
}

/** The API's `stage` parameter for a tab. */
export function stageParam(tab: StageTab) {
  return tab
}

export function useOutreachList(tab: StageTab | null, filters: OutreachFilters, researchHeld?: "0" | "1") {
  const on = useOutreachEnabled()
  const params: OutreachListParams = {
    ...filters,
    stage: tab ? stageParam(tab) : undefined,
    research_held: tab === "approved" ? researchHeld : undefined,
    per_page: 100,
    sort: tab === "candidate" ? "review" : "newest",
  }
  return useQuery({
    queryKey: ["outreach", "list", params],
    queryFn: () => outreachApi.list(params),
    enabled: on && !!tab,
    staleTime: 30 * 1000,
  })
}

/** The Inbox: Gmail threads, unread first. Refreshes on its own (the sync runs every two minutes). */
export function useOutreachInbox(view: InboxView, q: string, page: number) {
  const on = useOutreachEnabled()
  const params = { view, q, page, per_page: 50 }
  return useQuery({
    queryKey: ["outreach", "inbox", params],
    queryFn: () => outreachApi.inbox(params),
    enabled: on,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    placeholderData: (previous) => previous,
  })
}

/**
 * One page of the backlog, best first (the pipeline's score), optionally one
 * YC batch. The previous page stays on screen while the next loads.
 */
export function useOutreachBacklog(filters: OutreachFilters, page: number) {
  const on = useOutreachEnabled()
  const params: OutreachListParams = { ...filters, stage: "backlog", sort: "best", per_page: BACKLOG_PAGE, page }
  return useQuery({
    queryKey: ["outreach", "list", params],
    queryFn: () => outreachApi.list(params),
    enabled: on,
    staleTime: 30 * 1000,
    placeholderData: (previous) => previous,
  })
}

/**
 * How many approved prospects are held and how many are still being
 * researched, under the queue's filters: the Researching tab's sub-filter counts.
 */
export function useOutreachHoldCounts(filters: OutreachFilters, enabled = true) {
  const on = useOutreachEnabled()
  const count = (held: "0" | "1") => outreachApi.list({ ...filters, stage: "approved", research_held: held, per_page: 1 }).then((r) => r.meta.total)
  return useQuery({
    queryKey: ["outreach", "hold-counts", filters],
    queryFn: async () => {
      const [held, researching] = await Promise.all([count("1"), count("0")])
      return { held, researching }
    },
    enabled: on && enabled,
    staleTime: 30 * 1000,
  })
}

export function useOutreachProspect(id: number | null) {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "prospect", id],
    queryFn: () => outreachApi.get(id!),
    enabled: on && id !== null,
    staleTime: 15 * 1000,
  })
}

export function invalidateOutreach(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ["outreach"] })
}

/** Show an updated prospect everywhere it is cached. */
function patchCaches(queryClient: QueryClient, updated: OutreachProspect) {
  const today = localDay(new Date())
  queryClient.setQueriesData<{ date: string; groups: TodayGroups }>({ queryKey: ["outreach", "today"] }, (old) =>
    old ? { ...old, groups: regroupToday(old.groups, updated, old.date ?? today) } : old,
  )
  for (const [key, page] of queryClient.getQueriesData<OutreachPage>({ queryKey: ["outreach", "list"] })) {
    if (!page) continue
    const params = key[2] as OutreachListParams | undefined
    const stage = params?.stage
    const heldMatches = !params?.research_held || (params.research_held === "1") === !!updated.research_held
    const belongs = (!stage || stage === tabOf(updated.stage)) && heldMatches
    queryClient.setQueryData<OutreachPage>(key, {
      ...page,
      data: belongs ? page.data.map((p) => (p.id === updated.id ? updated : p)) : page.data.filter((p) => p.id !== updated.id),
    })
  }
  queryClient.setQueryData<OutreachProspect>(["outreach", "prospect", updated.id], (old) => (old ? { ...old, ...updated, events: updated.events ?? old.events } : old))
}

const DONE: Partial<Record<OutreachAction, string>> = {
  approve: "Approved, research is next",
  schedule_send: "Approved, Forge sends it at its time",
  cancel_send: "Send canceled, it's a draft again",
  clear_inbox: "Marked done",
  mark_sent: "Marked sent",
  linkedin_invited: "Invite marked sent",
  linkedin_accepted: "Marked accepted",
  followup_sent: "Follow-up marked sent",
  replied: "Reply logged",
  skip: "Skipped",
  hand_to_justin: "Handed to Justin",
  convert_to_prospect: "Converted to a client prospect",
  reopen: "Reopened",
  resume_research: "Sent back to research",
  pull: "Pulled into To review",
}

export function useOutreachActions() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const run = useMutation({
    mutationFn: ({ prospect, action, body }: { prospect: OutreachProspect; action: OutreachAction; body?: OutreachActionBody }) =>
      outreachApi.action(prospect.id, action, body),
    onMutate: async ({ prospect, action, body }) => {
      await queryClient.cancelQueries({ queryKey: ["outreach"] })
      patchCaches(queryClient, applyActionLocally(prospect, action, { ...body, text: body?.text ?? undefined, note: body?.note ?? undefined }))
    },
    onSuccess: (updated, { action }) => {
      patchCaches(queryClient, updated)
      if (DONE[action]) toast({ title: DONE[action], description: updated.company })
    },
    onError: (error, { action }) => toast({ variant: "destructive", title: `Could not ${action.replace(/_/g, " ")}`, description: getApiErrorMessage(error) }),
    onSettled: () => invalidateOutreach(queryClient),
  })

  const bulk = useMutation({
    mutationFn: ({ action, prospects, body }: { action: OutreachAction; prospects: OutreachProspect[]; body?: OutreachActionBody }) =>
      outreachApi.bulk(action, prospects.map((p) => p.id), body),
    onMutate: async ({ action, prospects, body }) => {
      await queryClient.cancelQueries({ queryKey: ["outreach"] })
      for (const p of prospects) patchCaches(queryClient, applyActionLocally(p, action, { reason: body?.reason }))
    },
    onSuccess: (result, { action }) => {
      if (result.failed.length) {
        toast({ variant: "destructive", title: `${result.done.length} done, ${result.failed.length} refused`, description: result.failed[0].message })
      } else {
        toast({ title: `${DONE[action] ?? "Done"}: ${result.done.length}` })
      }
    },
    onError: (error) => toast({ variant: "destructive", title: "Could not update them", description: getApiErrorMessage(error) }),
    onSettled: () => invalidateOutreach(queryClient),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<OutreachProspect> }) => outreachApi.update(id, body),
    onSuccess: (updated) => patchCaches(queryClient, updated),
    onError: (error) => toast({ variant: "destructive", title: "Could not save", description: getApiErrorMessage(error) }),
    onSettled: () => invalidateOutreach(queryClient),
  })

  /** A reply or follow-up in the thread; it goes after the undo window. */
  const write = useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => outreachApi.sendMessage(id, body),
    onSuccess: (updated) => {
      patchCaches(queryClient, updated)
      toast({ title: "Reply scheduled", description: "It goes in a few minutes. Cancel it from the thread until then." })
    },
    onError: (error) => toast({ variant: "destructive", title: "Could not send the reply", description: getApiErrorMessage(error) }),
    onSettled: () => invalidateOutreach(queryClient),
  })

  const cancelMessage = useMutation({
    mutationFn: (messageId: number) => outreachApi.cancelMessage(messageId),
    onSuccess: (updated) => {
      patchCaches(queryClient, updated)
      toast({ title: "Canceled", description: "That email will not go." })
    },
    onError: (error) => toast({ variant: "destructive", title: "Could not cancel", description: getApiErrorMessage(error) }),
    onSettled: () => invalidateOutreach(queryClient),
  })

  return { run, bulk, update, write, cancelMessage }
}

/* ------------------------------------------------------------------------ */
/* Gmail                                                                     */
/* ------------------------------------------------------------------------ */

export function useOutreachGmail() {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "gmail"],
    queryFn: () => outreachApi.gmail(),
    enabled: on,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })
}

export function useGmailConnection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Google's consent screen takes over the tab and comes back to /outreach/settings?gmail=…
  const connect = useMutation({
    mutationFn: () => outreachApi.gmailAuthUrl(),
    onSuccess: (url) => window.location.assign(url),
    onError: (error) => toast({ variant: "destructive", title: "Could not start connecting Gmail", description: getApiErrorMessage(error) }),
  })

  const disconnect = useMutation({
    mutationFn: () => outreachApi.disconnectGmail(),
    onSuccess: (status) => {
      queryClient.setQueryData(["outreach", "gmail"], status)
      toast({ title: "Gmail disconnected", description: "Approved emails wait until it is connected again." })
    },
    onError: (error) => toast({ variant: "destructive", title: "Could not disconnect Gmail", description: getApiErrorMessage(error) }),
    onSettled: () => invalidateOutreach(queryClient),
  })

  return { connect, disconnect }
}

/* ------------------------------------------------------------------------ */
/* Runs                                                                      */
/* ------------------------------------------------------------------------ */

export function useOutreachRuns(filters: OutreachRunFilters) {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "runs", filters],
    queryFn: () => outreachApi.runs(filters),
    enabled: on,
    staleTime: 30 * 1000,
    placeholderData: (previous) => previous,
  })
}

export function useOutreachRun(id: number | null) {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "run", id],
    queryFn: () => outreachApi.run(id!),
    enabled: on && id !== null,
    staleTime: 30 * 1000,
  })
}

export function useOutreachRunsSummary() {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "runs-summary"],
    queryFn: () => outreachApi.runsSummary(),
    enabled: on,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}

/* ------------------------------------------------------------------------ */
/* Settings                                                                  */
/* ------------------------------------------------------------------------ */

export function useOutreachSettings() {
  const on = useOutreachEnabled()
  return useQuery({
    queryKey: ["outreach", "settings"],
    queryFn: () => outreachApi.settings(),
    enabled: on,
    staleTime: 5 * 60 * 1000,
  })
}

/** The raise-window thresholds the API classifies by; null until settings load. */
export function useWindowThresholds() {
  return windowThresholds(useOutreachSettings().data)
}

export function useSaveOutreachSettings() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const settled = (updated: OutreachSettings) => {
    queryClient.setQueryData(["outreach", "settings"], updated)
    // The window thresholds and the jobs switch change what the queue and the header show.
    invalidateOutreach(queryClient)
  }

  const save = useMutation({
    mutationFn: (body: OutreachSettingsBody) => outreachApi.saveSettings(body),
    onSuccess: (updated) => {
      settled(updated)
      toast({ title: "Settings saved" })
    },
    onError: (error) => toast({ variant: "destructive", title: "Could not save the settings", description: getApiErrorMessage(error) }),
  })

  const tick = useMutation({
    mutationFn: ({ key, done }: { key: string; done: boolean }) => outreachApi.setChecklistItem(key, done),
    onMutate: async ({ key, done }) => {
      await queryClient.cancelQueries({ queryKey: ["outreach", "settings"] })
      queryClient.setQueryData<OutreachSettings>(["outreach", "settings"], (old) =>
        old
          ? {
              ...old,
              checklist: old.checklist.map((i) => (i.key === key ? { ...i, done, done_at: done ? new Date().toISOString() : null } : i)),
              open_items: old.open_items + (done ? -1 : 1),
            }
          : old,
      )
    },
    onSuccess: (updated) => queryClient.setQueryData(["outreach", "settings"], updated),
    onError: (error) => {
      toast({ variant: "destructive", title: "Could not update the checklist", description: getApiErrorMessage(error) })
      queryClient.invalidateQueries({ queryKey: ["outreach", "settings"] })
    },
  })

  return { save, tick }
}
